/**
 * Track H — canvas preview component.
 *
 * Pan (drag / space+drag / middle-drag), zoom-to-cursor (wheel; ctrl+wheel is
 * treated as trackpad pinch-zoom, never rotate), dbl-click / 0 fit, 1 = 100%,
 * R / Shift+R rotate the view by 15deg, devicePixelRatio aware. The view
 * transform is inspection-only — it never mutates exported geometry.
 *
 * Render pipeline (dirty-flagged rAF loop):
 *   clear -> whole-mm grid paper (when zoomed in) -> source contours (1px
 *   screen-space strokes, layer color @ 40% alpha) -> pixels grouped by layer,
 *   one Path2D per layer filled evenodd in a single call. Full dots are drawn
 *   as parametric shapes; edge-clipped pixels via their explicit rings.
 *
 * Geometry Path2Ds are cached in WORLD (mm) coordinates and reused across
 * frames; only the canvas transform changes on pan/zoom/rotate, so interaction
 * stays smooth even at the render cap. Above 150k pixels the preview is
 * stride-sampled and `lastRenderSampled` (plus a `data-render-sampled`
 * attribute on the canvas) is set so the shell can surface a notice.
 */
import type { BBox, Mat, Pt, Pixel, PolyGeom } from "../core/types";
import { LIGHTBURN_PALETTE } from "../core/types";

export interface CanvasContent {
  /** Contours to stroke (source geometry). */
  geoms: readonly PolyGeom[];
  /** Pixels to fill. */
  pixels: readonly Pixel[];
  /** layer -> "#RRGGBB". */
  layerColors: Record<number, string>;
  pixelSizeMm: number;
  shape: string;
  /** Vertical stretch (height ÷ width) applied to parametric dot shapes. */
  aspect?: number;
}

export interface ViewInfo {
  zoom: number;
  rotationDeg: number;
}

/** Public snapshot of the current view for external toolbars/state. */
export interface ViewSnapshot {
  zoom: number;
  panX: number;
  panY: number;
  rotationDeg: number;
  /** True when the view is exactly the fit-to-content default. */
  fitted: boolean;
}

// ---------------------------------------------------------------------------
// Pure view-transform math (exported for unit tests; used by the class below).
//
// The world->screen map is an affine matrix Mat = [a,b,c,d,e,f] with
// (x,y) -> (a*x + c*y + e, b*x + d*y + f), matching src/core/types Mat.
// `zoom` is a fit-relative multiplier: 1 == the fit-to-content scale, so the
// effective screen scale is fitBaseScale * zoom. All screen units are CSS px.
// ---------------------------------------------------------------------------

export const VIEW_MARGIN_FRAC = 0.08;
export const ZOOM_MIN = 0.05;
export const ZOOM_MAX = 64;

export interface ViewState {
  zoom: number;
  panX: number;
  panY: number;
  rotationDeg: number;
}

function clampNum(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Screen-px-per-mm that fits `bbox` (rotated by rotationDeg) inside viewW x viewH. */
export function fitBaseScale(
  bbox: BBox,
  viewW: number,
  viewH: number,
  rotationDeg: number,
  marginFrac: number = VIEW_MARGIN_FRAC,
): number {
  const cx = (bbox.minX + bbox.maxX) / 2;
  const cy = (bbox.minY + bbox.maxY) / 2;
  const th = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  let halfW = 0;
  let halfH = 0;
  const xs = [bbox.minX, bbox.maxX];
  const ys = [bbox.minY, bbox.maxY];
  for (const x of xs) {
    for (const y of ys) {
      const rx = (x - cx) * cos - (y - cy) * sin;
      const ry = (x - cx) * sin + (y - cy) * cos;
      if (Math.abs(rx) > halfW) halfW = Math.abs(rx);
      if (Math.abs(ry) > halfH) halfH = Math.abs(ry);
    }
  }
  const fullW = Math.max(2 * halfW, 1e-6);
  const fullH = Math.max(2 * halfH, 1e-6);
  const availW = Math.max(1, viewW * (1 - 2 * marginFrac));
  const availH = Math.max(1, viewH * (1 - 2 * marginFrac));
  const s = Math.min(availW / fullW, availH / fullH);
  return Number.isFinite(s) && s > 0 ? s : 1;
}

/** Full world(mm)->screen(CSS px) affine for the given content + view. */
export function viewMatrix(
  bbox: BBox,
  viewW: number,
  viewH: number,
  view: ViewState,
  marginFrac: number = VIEW_MARGIN_FRAC,
): Mat {
  const base = fitBaseScale(bbox, viewW, viewH, view.rotationDeg, marginFrac);
  const zoom = Number.isFinite(view.zoom) && view.zoom > 0 ? view.zoom : 1;
  const s = base * zoom;
  const th = (view.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  const cx = (bbox.minX + bbox.maxX) / 2;
  const cy = (bbox.minY + bbox.maxY) / 2;
  const a = s * cos;
  const b = s * sin;
  const c = -s * sin;
  const d = s * cos;
  const scx = viewW / 2 + view.panX;
  const scy = viewH / 2 + view.panY;
  const e = scx - a * cx - c * cy;
  const f = scy - b * cx - d * cy;
  return [a, b, c, d, e, f];
}

export function worldToScreen(m: Mat, x: number, y: number): Pt {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

export function screenToWorld(m: Mat, sx: number, sy: number): Pt {
  const a = m[0];
  const b = m[1];
  const c = m[2];
  const d = m[3];
  const det = a * d - b * c || 1e-12;
  const X = sx - m[4];
  const Y = sy - m[5];
  return { x: (d * X - c * Y) / det, y: (-b * X + a * Y) / det };
}

/** Fit-to-content matrix (zoom 1, pan 0) — the default centered view. */
export function fitTransform(
  bbox: BBox,
  viewW: number,
  viewH: number,
  rotationDeg: number = 0,
  marginFrac: number = VIEW_MARGIN_FRAC,
): Mat {
  return viewMatrix(bbox, viewW, viewH, { zoom: 1, panX: 0, panY: 0, rotationDeg }, marginFrac);
}

/**
 * Pan that keeps a world point pinned under a screen point at the given zoom +
 * rotation — the core of zoom/rotate-to-cursor. Returns fresh {panX, panY}.
 */
export function panToPin(
  bbox: BBox,
  viewW: number,
  viewH: number,
  zoom: number,
  rotationDeg: number,
  worldX: number,
  worldY: number,
  screenX: number,
  screenY: number,
  marginFrac: number = VIEW_MARGIN_FRAC,
): { panX: number; panY: number } {
  const base = fitBaseScale(bbox, viewW, viewH, rotationDeg, marginFrac);
  const s = base * (Number.isFinite(zoom) && zoom > 0 ? zoom : 1);
  const th = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  const a = s * cos;
  const b = s * sin;
  const c = -s * sin;
  const d = s * cos;
  const cx = (bbox.minX + bbox.maxX) / 2;
  const cy = (bbox.minY + bbox.maxY) / 2;
  const panX = screenX - viewW / 2 - a * (worldX - cx) - c * (worldY - cy);
  const panY = screenY - viewH / 2 - b * (worldX - cx) - d * (worldY - cy);
  return { panX, panY };
}

// ---------------------------------------------------------------------------
// Local pixel-shape drawing (matches src/core/geometry pixelShapeRings, but
// implemented here so the preview never imports engine internals). Cross is
// drawn as a single 12-vertex plus outline instead of two overlapping rects so
// it renders solid under the layer's single even-odd fill (see class docs).
// ---------------------------------------------------------------------------

// `ys` is the signed vertical scale (aspect × flip): every y-offset from the
// center is multiplied by it, so shapes stretch and mirror consistently.
function polygonSubpath(path: Path2D, cx: number, cy: number, r: number, n: number, rot: number, ys: number): void {
  for (let i = 0; i < n; i++) {
    const a = rot + (2 * Math.PI * i) / n;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r * ys;
    if (i === 0) path.moveTo(x, y);
    else path.lineTo(x, y);
  }
  path.closePath();
}

function starSubpath(path: Path2D, cx: number, cy: number, rOuter: number, rInner: number, points: number, rot: number, ys: number): void {
  for (let i = 0; i < points * 2; i++) {
    const a = rot + (Math.PI * i) / points;
    const rr = i % 2 === 0 ? rOuter : rInner;
    const x = cx + Math.cos(a) * rr;
    const y = cy + Math.sin(a) * rr * ys;
    if (i === 0) path.moveTo(x, y);
    else path.lineTo(x, y);
  }
  path.closePath();
}

function heartSubpath(path: Path2D, cx: number, cy: number, r: number, ys: number): void {
  const n = 48;
  let ext = 0;
  const px: number[] = [];
  const py: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = (2 * Math.PI * i) / n;
    const x = 16 * Math.pow(Math.sin(t), 3);
    const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
    px.push(x);
    py.push(y);
    ext = Math.max(ext, Math.abs(x), Math.abs(y));
  }
  const s = ext > 0 ? r / ext : 1;
  for (let i = 0; i < n; i++) {
    const x = cx + px[i] * s;
    const y = cy + py[i] * s * ys;
    if (i === 0) path.moveTo(x, y);
    else path.lineTo(x, y);
  }
  path.closePath();
}

function crossSubpath(path: Path2D, cx: number, cy: number, r: number, size: number, ys: number): void {
  const bar = Math.max(size * 0.33, size / 6);
  const hb = bar / 2;
  const y = (dy: number): number => cy + dy * ys;
  path.moveTo(cx - hb, y(-r));
  path.lineTo(cx + hb, y(-r));
  path.lineTo(cx + hb, y(-hb));
  path.lineTo(cx + r, y(-hb));
  path.lineTo(cx + r, y(hb));
  path.lineTo(cx + hb, y(hb));
  path.lineTo(cx + hb, y(r));
  path.lineTo(cx - hb, y(r));
  path.lineTo(cx - hb, y(hb));
  path.lineTo(cx - r, y(hb));
  path.lineTo(cx - r, y(-hb));
  path.lineTo(cx - hb, y(-hb));
  path.closePath();
}

function addShape(path: Path2D, shape: string, cx: number, cy: number, size: number, aspect: number, flipY: boolean): void {
  const r = size / 2;
  const ys = (flipY ? -1 : 1) * (aspect > 0 ? aspect : 1);
  switch (shape) {
    case "square":
      polygonSubpath(path, cx, cy, r, 4, Math.PI / 4, ys);
      break;
    case "diamond":
      polygonSubpath(path, cx, cy, r, 4, 0, ys);
      break;
    case "hexagon":
      polygonSubpath(path, cx, cy, r, 6, 0, ys);
      break;
    case "octagon":
      polygonSubpath(path, cx, cy, r, 8, Math.PI / 8, ys);
      break;
    case "triangle":
      polygonSubpath(path, cx, cy, r, 3, -Math.PI / 2, ys);
      break;
    case "star":
      starSubpath(path, cx, cy, r, r * 0.5, 5, -Math.PI / 2, ys);
      break;
    case "heart":
      heartSubpath(path, cx, cy, r, ys);
      break;
    case "cross":
      crossSubpath(path, cx, cy, r, size, ys);
      break;
    case "circle":
    default:
      if (aspect === 1) {
        path.moveTo(cx + r, cy);
        path.arc(cx, cy, r, 0, Math.PI * 2);
      } else {
        path.ellipse(cx, cy, r, r * Math.abs(ys), 0, 0, Math.PI * 2);
      }
      path.closePath();
      break;
  }
}

// ---------------------------------------------------------------------------
// Style injection (own CSS; never touches track G's style.css).
// ---------------------------------------------------------------------------

const CANVAS_STYLE_ID = "mopa-canvas-style";

function injectCanvasStyle(): void {
  if (typeof document === "undefined" || document.getElementById(CANVAS_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = CANVAS_STYLE_ID;
  el.textContent = `
.mopa-canvas-host { position: relative; }
.mopa-canvas {
  display: block;
  width: 100%;
  height: 100%;
  outline: none;
  touch-action: none;
  cursor: grab;
  background: transparent;
  image-rendering: auto;
}
.mopa-canvas.mopa-panning { cursor: grabbing; }
.mopa-canvas:focus-visible { outline: 2px solid var(--focus, #6aa9ff); outline-offset: -2px; }

/* On-canvas zoom overlay (touch/pointer fallback for gestures). */
.mopa-canvas-overlay {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 2;
  pointer-events: none;
}
.mopa-canvas-overlay button {
  pointer-events: auto;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  color: var(--text, #fff);
  background: var(--surface-2, rgba(24,26,32,0.82));
  border: 1px solid var(--border-strong, rgba(255,255,255,0.22));
  border-radius: 10px;
  cursor: pointer;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition: background 120ms ease, transform 120ms ease;
}
.mopa-canvas-overlay button:hover { background: var(--surface-3, rgba(40,44,54,0.92)); }
.mopa-canvas-overlay button:active { transform: scale(0.94); }
.mopa-canvas-overlay button:focus-visible { outline: 2px solid var(--focus, #6aa9ff); outline-offset: 2px; }
.mopa-canvas-overlay .mopa-overlay-label { font-size: 11px; font-weight: 700; }

/* Focus-only keyboard shortcut hint. */
.mopa-canvas-hint {
  position: absolute;
  left: 12px;
  bottom: 12px;
  max-width: min(360px, calc(100% - 80px));
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text, #fff);
  background: var(--surface-2, rgba(24,26,32,0.9));
  border: 1px solid var(--border-strong, rgba(255,255,255,0.22));
  border-radius: 10px;
  z-index: 2;
  opacity: 0;
  transform: translateY(6px);
  pointer-events: none;
  transition: opacity 140ms ease, transform 140ms ease;
}
.mopa-canvas-host.mopa-hint-visible .mopa-canvas-hint { opacity: 1; transform: none; }

/* Before/after compare divider handle. */
.mopa-compare-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  transform: translateX(-1px);
  background: var(--border-strong, rgba(255,255,255,0.85));
  z-index: 3;
  display: none;
  touch-action: none;
  cursor: ew-resize;
}
.mopa-canvas-host.mopa-compare-on .mopa-compare-handle { display: block; }
.mopa-compare-grip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--surface-2, rgba(24,26,32,0.92));
  border: 1px solid var(--border-strong, rgba(255,255,255,0.7));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: var(--text, #fff);
  cursor: ew-resize;
  pointer-events: auto;
}
.mopa-compare-grip:focus-visible { outline: 2px solid var(--focus, #6aa9ff); outline-offset: 2px; }
.mopa-compare-label {
  position: absolute;
  top: 10px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  background: var(--surface-2, rgba(24,26,32,0.85));
  border: 1px solid var(--border, rgba(255,255,255,0.18));
  color: var(--text, #fff);
  pointer-events: none;
  z-index: 3;
  display: none;
}
.mopa-canvas-host.mopa-compare-on .mopa-compare-label { display: block; }
.mopa-compare-label-before { left: 10px; }
.mopa-compare-label-after { right: 10px; }

@media (prefers-reduced-motion: reduce) {
  .mopa-canvas-overlay button,
  .mopa-canvas-hint { transition: none; }
}
`;
  document.head.appendChild(el);
}

// ---------------------------------------------------------------------------

interface LayerCache {
  layer: number;
  color: string;
  pixel: Path2D | null;
  contour: Path2D | null;
}

const RENDER_CAP = 150000;
const CONTOUR_CAP = 6000;
const CONTOUR_POINT_CAP = 1500;
const DEFAULT_BBOX: BBox = { minX: -10, minY: -10, maxX: 10, maxY: 10 };

/** Coarse identity of an artwork's extent — quantized so tiny numeric drift
 * between regenerations of the same artwork doesn't count as a new document. */
function contentBBoxKey(b: BBox): string {
  const q = (v: number): number => Math.round(v * 10) / 10;
  return `${q(b.minX)},${q(b.minY)},${q(b.maxX)},${q(b.maxY)}`;
}

/** One active pointer's most recent position (CSS px, relative to canvas rect). */
interface ActivePointer {
  x: number;
  y: number;
}

const KEY_PAN_STEP = 40; // CSS px per arrow-key press
const OVERLAY_ZOOM_STEP = 1.35; // multiplicative zoom per +/- button/key press

export class CanvasView {
  private host: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private onViewChange?: (v: ViewInfo) => void;

  private overlay: HTMLDivElement;
  private hint: HTMLDivElement;

  private content: CanvasContent | null = null;
  private extent: BBox | null = null;
  private layerCaches: LayerCache[] = [];

  private zoom = 1;
  private panX = 0;
  private panY = 0;
  private rotationDeg = 0;
  private fitted = false;
  private contentKey = "";

  private sampled = false;
  private sampleStride = 1;

  private cssW = 0;
  private cssH = 0;
  private dpr = 1;

  private dirty = false;
  private rafId = 0;
  private destroyed = false;

  // Multi-pointer tracking. A single pointer pans; two pointers pinch-zoom
  // around their centroid and pan by centroid motion. Positions are in CSS px
  // relative to the canvas bounding rect.
  private pointers = new Map<number, ActivePointer>();
  private lastX = 0; // last single-pointer position (canvas-relative CSS px)
  private lastY = 0;
  private pinchDist = 0; // last two-pointer distance
  private pinchCx = 0; // last two-pointer centroid x
  private pinchCy = 0; // last two-pointer centroid y

  // Before/after compare.
  private compareOn = false;
  private compareSource: CanvasImageSource | null = null;
  private compareSourceW = 0;
  private compareSourceH = 0;
  private compareFrac = 0.5; // divider position, 0..1 across canvas width
  private compareHandle: HTMLDivElement | null = null;
  private compareLabelBefore: HTMLDivElement | null = null;
  private compareLabelAfter: HTMLDivElement | null = null;
  private compareDragging = false;
  private compareHandlePointer = -1;
  private showOriginal = false; // press-and-hold "show original" override

  private colorsResolved = false;
  private gridMinor = "rgba(255,255,255,0.09)";
  private gridMajor = "rgba(255,255,255,0.17)";

  private resizeObserver: ResizeObserver | null = null;

  constructor(host: HTMLElement, onViewChange?: (v: ViewInfo) => void) {
    injectCanvasStyle();
    this.host = host;
    this.onViewChange = onViewChange;

    // The host is the positioning context for overlay + compare UI.
    host.classList.add("mopa-canvas-host");

    const canvas = document.createElement("canvas");
    canvas.className = "mopa-canvas";
    canvas.tabIndex = 0;
    // Keyboard-operable canvas: expose it as an interactive image with a
    // description of the pan/zoom shortcuts (also shown visually on focus).
    canvas.setAttribute("role", "application");
    canvas.setAttribute(
      "aria-label",
      "Design preview. Drag or use arrow keys to pan, plus/minus to zoom, 0 to fit.",
    );
    canvas.setAttribute("aria-roledescription", "Zoomable preview canvas");
    host.appendChild(canvas);
    this.canvas = canvas;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context is unavailable");
    this.ctx = ctx;

    // --- keyboard shortcut hint (visible on focus) ---
    const hint = document.createElement("div");
    hint.className = "mopa-canvas-hint";
    hint.setAttribute("aria-hidden", "true");
    hint.textContent = "Pan: drag or arrow keys · Zoom: + / − or scroll · Fit: 0 · 100%: 1";
    host.appendChild(hint);
    this.hint = hint;

    // --- on-canvas zoom overlay (touch/pointer fallback) ---
    const overlay = document.createElement("div");
    overlay.className = "mopa-canvas-overlay";
    overlay.setAttribute("role", "group");
    overlay.setAttribute("aria-label", "Preview zoom controls");
    const mkBtn = (label: string, aria: string, onClick: () => void, isText = false): HTMLButtonElement => {
      const b = document.createElement("button");
      b.type = "button";
      if (isText) {
        const span = document.createElement("span");
        span.className = "mopa-overlay-label";
        span.textContent = label;
        b.appendChild(span);
      } else {
        b.textContent = label;
      }
      b.setAttribute("aria-label", aria);
      b.title = aria;
      // Keep canvas focus/gestures unaffected by button interaction.
      b.addEventListener("pointerdown", (e) => e.stopPropagation());
      b.addEventListener("click", (e) => {
        e.preventDefault();
        onClick();
      });
      return b;
    };
    overlay.appendChild(mkBtn("+", "Zoom in", () => this.zoomIn()));
    overlay.appendChild(mkBtn("−", "Zoom out", () => this.zoomOut()));
    overlay.appendChild(mkBtn("Fit", "Fit to view", () => this.fitView(), true));
    overlay.appendChild(mkBtn("100%", "Zoom to 100 percent", () => this.resetZoom(), true));
    host.appendChild(overlay);
    this.overlay = overlay;

    // --- before/after compare divider ---
    const handle = document.createElement("div");
    handle.className = "mopa-compare-handle";
    handle.setAttribute("role", "slider");
    handle.setAttribute("aria-label", "Compare divider (original vs processed)");
    handle.setAttribute("aria-orientation", "horizontal");
    handle.setAttribute("aria-valuemin", "0");
    handle.setAttribute("aria-valuemax", "100");
    handle.tabIndex = 0;
    const grip = document.createElement("div");
    grip.className = "mopa-compare-grip";
    grip.textContent = "⇔";
    handle.appendChild(grip);
    handle.addEventListener("pointerdown", this.onCompareHandleDown);
    handle.addEventListener("pointermove", this.onCompareHandleMove);
    handle.addEventListener("pointerup", this.onCompareHandleUp);
    handle.addEventListener("pointercancel", this.onCompareHandleUp);
    handle.addEventListener("keydown", this.onCompareHandleKey);
    host.appendChild(handle);
    this.compareHandle = handle;

    const labelBefore = document.createElement("div");
    labelBefore.className = "mopa-compare-label mopa-compare-label-before";
    labelBefore.textContent = "Original";
    host.appendChild(labelBefore);
    this.compareLabelBefore = labelBefore;

    const labelAfter = document.createElement("div");
    labelAfter.className = "mopa-compare-label mopa-compare-label-after";
    labelAfter.textContent = "Processed";
    host.appendChild(labelAfter);
    this.compareLabelAfter = labelAfter;

    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerUp);
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("dblclick", this.onDblClick);
    canvas.addEventListener("keydown", this.onKeyDown);
    canvas.addEventListener("keyup", this.onKeyUp);
    canvas.addEventListener("focus", this.onFocus);
    canvas.addEventListener("blur", this.onBlur);

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(host);
    }
    this.updateCompareHandlePos();
    this.measure();
  }

  // --------------------------------------------------------------- public ---

  setContent(content: CanvasContent | null): void {
    // A substantially different content bbox means a new document/artwork —
    // refit instead of keeping a stale view (fitted persists across tweaks to
    // the SAME artwork so pan/zoom survives setting changes).
    const prevKey = this.contentKey;
    this.content = content;
    this.buildCaches();
    this.contentKey = content ? contentBBoxKey(this.effectiveBBox()) : "";
    if (!content) {
      this.fitted = false;
    } else if (!this.fitted || (prevKey && this.contentKey !== prevKey)) {
      this.fit();
    }
    this.markDirty();
  }

  fit(): void {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.rotationDeg = 0;
    this.fitted = true;
    this.emitView();
    this.markDirty();
  }

  resetView(): void {
    this.fit();
  }

  // --------------------------------------------------- public view controls ---
  // Exposed for external toolbars (T3 desktop zoom toolbar / keyboard toggles).

  /** Fit content to the viewport (same as double-click / 0). */
  fitView(): void {
    this.fit();
  }

  /**
   * Reset to 100% — a true 1:1 physical scale (1 CSS px per mm), centered, no
   * rotation. This is the honest physical-scale view a maker expects from a
   * "100%" button (independent of the fit-relative zoom baseline).
   */
  resetZoom(): void {
    this.setZoomTo100Percent();
  }

  /** Zoom in one step around the viewport center. */
  zoomIn(): void {
    this.zoomAround(this.cssW / 2, this.cssH / 2, OVERLAY_ZOOM_STEP);
  }

  /** Zoom out one step around the viewport center. */
  zoomOut(): void {
    this.zoomAround(this.cssW / 2, this.cssH / 2, 1 / OVERLAY_ZOOM_STEP);
  }

  /** Current view snapshot (zoom/pan/rotation + fitted flag). */
  getView(): ViewSnapshot {
    return {
      zoom: this.zoom,
      panX: this.panX,
      panY: this.panY,
      rotationDeg: this.rotationDeg,
      fitted: this.isFittedView(),
    };
  }

  // ------------------------------------------------ before/after compare API ---

  /**
   * Provide (or clear) the source image shown in compare mode. Pass the decoded
   * source (e.g. an ImageBitmap or HTMLImageElement) to reveal the untouched
   * artwork under the divider; pass null to drop it. Turning compare on without
   * a source shows a plain "original" placeholder region (empty).
   */
  setCompareSource(image: CanvasImageSource | null): void {
    this.compareSource = image;
    if (image) {
      const anyImg = image as { width?: number; height?: number };
      this.compareSourceW = typeof anyImg.width === "number" ? anyImg.width : 0;
      this.compareSourceH = typeof anyImg.height === "number" ? anyImg.height : 0;
    } else {
      this.compareSourceW = 0;
      this.compareSourceH = 0;
    }
    this.markDirty();
  }

  /** Enable/disable the draggable before/after divider compare mode. */
  setCompareMode(on: boolean): void {
    if (this.compareOn === on) return;
    this.compareOn = on;
    this.host.classList.toggle("mopa-compare-on", on);
    if (on) this.updateCompareHandlePos();
    this.markDirty();
  }

  /** Whether compare mode is currently active. */
  get compareEnabled(): boolean {
    return this.compareOn;
  }

  /**
   * Press-and-hold "show original": while true, the whole preview shows the
   * source image (no divider). T3 wires this to a hold affordance / hotkey.
   */
  setShowOriginal(on: boolean): void {
    if (this.showOriginal === on) return;
    this.showOriginal = on;
    this.markDirty();
  }

  /** True when the last built preview dropped pixels to stay under the render cap. */
  get lastRenderSampled(): boolean {
    return this.sampled;
  }

  destroy(): void {
    this.destroyed = true;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerUp);
    this.canvas.removeEventListener("wheel", this.onWheel);
    this.canvas.removeEventListener("dblclick", this.onDblClick);
    this.canvas.removeEventListener("keydown", this.onKeyDown);
    this.canvas.removeEventListener("keyup", this.onKeyUp);
    this.canvas.removeEventListener("focus", this.onFocus);
    this.canvas.removeEventListener("blur", this.onBlur);
    if (this.compareHandle) {
      this.compareHandle.removeEventListener("pointerdown", this.onCompareHandleDown);
      this.compareHandle.removeEventListener("pointermove", this.onCompareHandleMove);
      this.compareHandle.removeEventListener("pointerup", this.onCompareHandleUp);
      this.compareHandle.removeEventListener("pointercancel", this.onCompareHandleUp);
      this.compareHandle.removeEventListener("keydown", this.onCompareHandleKey);
    }
    this.overlay.remove();
    this.hint.remove();
    this.compareHandle?.remove();
    this.compareLabelBefore?.remove();
    this.compareLabelAfter?.remove();
    this.canvas.remove();
    this.host.classList.remove("mopa-canvas-host", "mopa-compare-on", "mopa-hint-visible");
    this.pointers.clear();
    this.content = null;
    this.compareSource = null;
    this.layerCaches = [];
  }

  // -------------------------------------------------------- view helpers ----
  //
  // Zoom controls are instantaneous (no JS-driven tween), so there is no forced
  // motion to suppress; the only transitions are the overlay/hint CSS ones,
  // which the injected stylesheet disables under prefers-reduced-motion.

  /** True when the current view equals the fit-to-content default. */
  private isFittedView(): boolean {
    return (
      this.fitted &&
      this.zoom === 1 &&
      this.panX === 0 &&
      this.panY === 0 &&
      this.rotationDeg === 0
    );
  }

  /** Center content at a true 1:1 (1 CSS px per mm) physical scale. */
  private setZoomTo100Percent(): void {
    const bbox = this.effectiveBBox();
    const w = this.cssW || 1;
    const h = this.cssH || 1;
    // The effective screen scale is fitBaseScale * zoom; solve for the zoom that
    // yields exactly 1 px/mm, then clamp to the allowed zoom range.
    const base = fitBaseScale(bbox, w, h, 0, VIEW_MARGIN_FRAC);
    const target = clampNum(1 / (base || 1), ZOOM_MIN, ZOOM_MAX);
    this.rotationDeg = 0;
    this.zoom = target;
    this.panX = 0;
    this.panY = 0;
    this.fitted = false;
    this.emitView();
    this.markDirty();
  }

  // ------------------------------------------------------------ internals ---

  private viewState(): ViewState {
    return { zoom: this.zoom, panX: this.panX, panY: this.panY, rotationDeg: this.rotationDeg };
  }

  private effectiveBBox(): BBox {
    return this.extent ?? DEFAULT_BBOX;
  }

  private matrix(): Mat {
    return viewMatrix(this.effectiveBBox(), this.cssW || 1, this.cssH || 1, this.viewState(), VIEW_MARGIN_FRAC);
  }

  private emitView(): void {
    if (this.onViewChange) this.onViewChange({ zoom: this.zoom, rotationDeg: this.rotationDeg });
  }

  private layerColor(content: CanvasContent, layer: number): string {
    const c = content.layerColors[layer];
    if (c) return c;
    return LIGHTBURN_PALETTE[((layer % 30) + 30) % 30] ?? "#B4B4B4";
  }

  private applySampledAttr(): void {
    this.canvas.dataset.renderSampled = this.sampled ? "true" : "false";
    if (this.sampled) this.canvas.dataset.renderStride = String(this.sampleStride);
    else delete this.canvas.dataset.renderStride;
  }

  private buildCaches(): void {
    this.layerCaches = [];
    this.sampled = false;
    this.sampleStride = 1;
    const content = this.content;
    if (!content) {
      this.extent = null;
      this.applySampledAttr();
      return;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const bump = (x: number, y: number): void => {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    };

    interface Group {
      color: string;
      pixel: Path2D;
      contour: Path2D;
      hasPixel: boolean;
      hasContour: boolean;
    }
    const groups = new Map<number, Group>();
    const groupFor = (layer: number): Group => {
      let g = groups.get(layer);
      if (!g) {
        g = {
          color: this.layerColor(content, layer),
          pixel: new Path2D(),
          contour: new Path2D(),
          hasPixel: false,
          hasContour: false,
        };
        groups.set(layer, g);
      }
      return g;
    };

    // Source contours: decimate long polylines, cap total count.
    let contourBudget = CONTOUR_CAP;
    for (const geom of content.geoms) {
      const pts = geom.points;
      const count = pts.length >> 1;
      if (count < 2) continue;
      if (contourBudget <= 0) break;
      contourBudget--;
      const g = groupFor(geom.layer);
      g.hasContour = true;
      const stride = Math.max(1, Math.floor(count / CONTOUR_POINT_CAP));
      let first = true;
      let i = 0;
      for (; i < count; i += stride) {
        const x = pts[i * 2];
        const y = pts[i * 2 + 1];
        bump(x, y);
        if (first) {
          g.contour.moveTo(x, y);
          first = false;
        } else {
          g.contour.lineTo(x, y);
        }
      }
      // Ensure the final vertex is present so the contour bbox is accurate.
      const lastX = pts[(count - 1) * 2];
      const lastY = pts[(count - 1) * 2 + 1];
      g.contour.lineTo(lastX, lastY);
      bump(lastX, lastY);
      if (geom.closed) g.contour.closePath();
    }

    // Pixels: extent over ALL pixels (stable fit), Path2D over the strided set.
    const pixels = content.pixels;
    const total = pixels.length;
    const stride = total > RENDER_CAP ? Math.ceil(total / RENDER_CAP) : 1;
    this.sampleStride = stride;
    this.sampled = stride > 1;
    const shape = content.shape;
    const pxSize = content.pixelSizeMm;
    const aspect = content.aspect ?? 1;

    for (let i = 0; i < total; i++) {
      const p = pixels[i];
      const emit = i % stride === 0;
      const rings = p.rings;
      if (rings && rings.length) {
        const g = emit ? groupFor(p.layer) : null;
        if (g) g.hasPixel = true;
        for (const ring of rings) {
          const rn = ring.length;
          if (rn < 6) continue;
          bump(ring[0], ring[1]);
          if (g) g.pixel.moveTo(ring[0], ring[1]);
          for (let k = 2; k < rn; k += 2) {
            bump(ring[k], ring[k + 1]);
            if (g) g.pixel.lineTo(ring[k], ring[k + 1]);
          }
          if (g) g.pixel.closePath();
        }
      } else {
        const size = pxSize * clampNum(p.sizeFactor, 0.05, 10);
        const h = size / 2;
        bump(p.x - h, p.y - h);
        bump(p.x + h, p.y + h);
        if (emit) {
          const g = groupFor(p.layer);
          g.hasPixel = true;
          addShape(g.pixel, shape, p.x, p.y, size, aspect, p.flipY ?? false);
        }
      }
    }

    if (!Number.isFinite(minX)) {
      this.extent = { ...DEFAULT_BBOX };
    } else {
      const pad = Math.max(1e-4, pxSize * 0.5);
      this.extent = { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
    }

    for (const [layer, g] of groups) {
      this.layerCaches.push({
        layer,
        color: g.color,
        pixel: g.hasPixel ? g.pixel : null,
        contour: g.hasContour ? g.contour : null,
      });
    }
    this.layerCaches.sort((a, b) => a.layer - b.layer);
    this.applySampledAttr();
  }

  private resolveColors(): void {
    if (typeof getComputedStyle === "undefined") return;
    const cs = getComputedStyle(this.canvas);
    const minor = cs.getPropertyValue("--border").trim();
    const major = cs.getPropertyValue("--border-strong").trim();
    if (minor) this.gridMinor = minor;
    if (major) this.gridMajor = major;
  }

  private measure(): void {
    if (this.destroyed) return;
    const w = Math.max(0, Math.floor(this.host.clientWidth));
    const h = Math.max(0, Math.floor(this.host.clientHeight));
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
    this.cssW = w;
    this.cssH = h;
    this.dpr = dpr;
    const bw = Math.max(1, Math.round(w * dpr));
    const bh = Math.max(1, Math.round(h * dpr));
    if (this.canvas.width !== bw) this.canvas.width = bw;
    if (this.canvas.height !== bh) this.canvas.height = bh;
    this.markDirty();
  }

  private markDirty(): void {
    if (this.destroyed) return;
    this.dirty = true;
    if (this.rafId === 0 && typeof requestAnimationFrame !== "undefined") {
      this.rafId = requestAnimationFrame(this.frame);
    }
  }

  private frame = (): void => {
    this.rafId = 0;
    if (this.destroyed || !this.dirty) return;
    this.dirty = false;
    this.render();
  };

  private render(): void {
    if (this.destroyed) return;
    const ctx = this.ctx;
    const canvas = this.canvas;
    if (!this.colorsResolved) {
      this.resolveColors();
      this.colorsResolved = true;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (this.cssW <= 0 || this.cssH <= 0) return;

    const m = this.matrix();
    const scale = Math.hypot(m[0], m[1]);
    if (!(scale > 0) || !Number.isFinite(scale)) return;
    const dpr = this.dpr;
    const setWorld = (): void => {
      ctx.setTransform(dpr * m[0], dpr * m[1], dpr * m[2], dpr * m[3], dpr * m[4], dpr * m[5]);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
    };
    setWorld();

    this.drawGrid(scale, m);

    // --- compare mode: "before" (source image) under a divider ---
    // showOriginal forces the whole canvas to the source; compare splits it.
    const compareActive = this.showOriginal || (this.compareOn && this.compareSource != null);
    // Device-space divider x (0..canvas.width). showOriginal => whole canvas is
    // "before" so the divider sits at the right edge (all source, no processed).
    const dividerDeviceX = this.showOriginal
      ? this.canvas.width
      : clampNum(this.compareFrac, 0, 1) * this.cssW * dpr;

    if (compareActive) {
      // Left of the divider: draw the source image, aligned to the content
      // extent in world space so it overlays the processed geometry 1:1.
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.beginPath();
      ctx.rect(0, 0, dividerDeviceX, this.canvas.height);
      ctx.clip();
      setWorld();
      this.drawCompareSource();
      ctx.restore();
      setWorld();
    }

    // --- processed geometry, clipped to the "after" region when comparing ---
    const clipProcessed = compareActive;
    if (clipProcessed) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.beginPath();
      ctx.rect(dividerDeviceX, 0, this.canvas.width - dividerDeviceX, this.canvas.height);
      ctx.clip();
      setWorld();
    }

    // Source contours — 1px screen-space strokes at 40% alpha.
    ctx.globalAlpha = 0.4;
    ctx.lineWidth = 1 / scale;
    for (const c of this.layerCaches) {
      if (c.contour) {
        ctx.strokeStyle = c.color;
        ctx.stroke(c.contour);
      }
    }
    ctx.globalAlpha = 1;

    // Pixels — one even-odd fill per layer.
    for (const c of this.layerCaches) {
      if (c.pixel) {
        ctx.fillStyle = c.color;
        ctx.fill(c.pixel, "evenodd");
      }
    }

    if (clipProcessed) ctx.restore();

    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  /** Draw the compare source image over the content extent (world space). */
  private drawCompareSource(): void {
    const img = this.compareSource;
    if (!img) return;
    const bb = this.effectiveBBox();
    const w = bb.maxX - bb.minX;
    const h = bb.maxY - bb.minY;
    if (!(w > 0) || !(h > 0)) return;
    const ctx = this.ctx;
    ctx.save();
    // Contain-fit the image into the content extent, centered, preserving aspect.
    const iw = this.compareSourceW || w;
    const ih = this.compareSourceH || h;
    const s = Math.min(w / iw, h / ih);
    const dw = iw * s;
    const dh = ih * s;
    const dx = bb.minX + (w - dw) / 2;
    const dy = bb.minY + (h - dh) / 2;
    try {
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(img, dx, dy, dw, dh);
    } catch {
      /* drawImage can throw on a not-yet-decoded source; ignore this frame */
    }
    ctx.restore();
  }

  private drawGrid(scale: number, m: Mat): void {
    const ctx = this.ctx;
    const c0 = screenToWorld(m, 0, 0);
    const c1 = screenToWorld(m, this.cssW, 0);
    const c2 = screenToWorld(m, 0, this.cssH);
    const c3 = screenToWorld(m, this.cssW, this.cssH);
    const wMinX = Math.min(c0.x, c1.x, c2.x, c3.x);
    const wMaxX = Math.max(c0.x, c1.x, c2.x, c3.x);
    const wMinY = Math.min(c0.y, c1.y, c2.y, c3.y);
    const wMaxY = Math.max(c0.y, c1.y, c2.y, c3.y);
    const lw = 1 / scale;

    const level = (step: number, color: string): void => {
      if ((wMaxX - wMinX) / step > 2000 || (wMaxY - wMinY) / step > 2000) return;
      ctx.strokeStyle = color;
      ctx.lineWidth = lw;
      ctx.beginPath();
      const x0 = Math.floor(wMinX / step) * step;
      for (let x = x0; x <= wMaxX; x += step) {
        ctx.moveTo(x, wMinY);
        ctx.lineTo(x, wMaxY);
      }
      const y0 = Math.floor(wMinY / step) * step;
      for (let y = y0; y <= wMaxY; y += step) {
        ctx.moveTo(wMinX, y);
        ctx.lineTo(wMaxX, y);
      }
      ctx.stroke();
    };

    // Minor 1mm lines only at high zoom; major 10mm lines earlier. Minor first
    // so the major grid reads on top of it.
    if (scale >= 7) level(1, this.gridMinor);
    if (scale * 10 >= 24) level(10, this.gridMajor);
  }

  private wheelFactor(e: WheelEvent): number {
    let d = e.deltaY;
    if (e.deltaMode === 1) d *= 16;
    else if (e.deltaMode === 2) d *= this.cssH || 800;
    d = clampNum(d, -160, 160);
    return Math.exp(-d * 0.0015);
  }

  private zoomAround(sx: number, sy: number, factor: number): void {
    const bbox = this.effectiveBBox();
    const w = this.cssW || 1;
    const h = this.cssH || 1;
    const m0 = viewMatrix(bbox, w, h, this.viewState(), VIEW_MARGIN_FRAC);
    const world = screenToWorld(m0, sx, sy);
    const nz = clampNum(this.zoom * factor, ZOOM_MIN, ZOOM_MAX);
    if (nz === this.zoom) return;
    const p = panToPin(bbox, w, h, nz, this.rotationDeg, world.x, world.y, sx, sy, VIEW_MARGIN_FRAC);
    this.zoom = nz;
    this.panX = p.panX;
    this.panY = p.panY;
    this.emitView();
    this.markDirty();
  }

  private rotateBy(deg: number): void {
    let d = (this.rotationDeg + deg) % 360;
    if (d > 180) d -= 360;
    if (d <= -180) d += 360;
    this.rotationDeg = d;
    this.emitView();
    this.markDirty();
  }

  private panBy(dx: number, dy: number): void {
    this.panX += dx;
    this.panY += dy;
    this.markDirty();
  }

  // -------------------------------------------------------------- events ---

  /** Canvas-relative CSS-px position of a pointer event. */
  private localPos(e: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  /** Recompute cached pinch centroid + distance from the two live pointers. */
  private refreshPinch(): void {
    const it = this.pointers.values();
    const a = it.next().value as ActivePointer | undefined;
    const b = it.next().value as ActivePointer | undefined;
    if (!a || !b) return;
    this.pinchCx = (a.x + b.x) / 2;
    this.pinchCy = (a.y + b.y) / 2;
    this.pinchDist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
  }

  private onPointerDown = (e: PointerEvent): void => {
    // Ignore secondary mouse buttons (allow left + middle for panning).
    if (e.pointerType === "mouse" && e.button !== 0 && e.button !== 1) return;
    try {
      this.canvas.focus({ preventScroll: true });
    } catch {
      /* focus is best-effort */
    }
    const p = this.localPos(e);
    this.pointers.set(e.pointerId, p);
    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort */
    }

    if (this.pointers.size === 1) {
      this.lastX = p.x;
      this.lastY = p.y;
      this.canvas.classList.add("mopa-panning");
    } else if (this.pointers.size === 2) {
      // Second finger down — establish the pinch baseline.
      this.refreshPinch();
      this.canvas.classList.remove("mopa-panning");
    }
    e.preventDefault();
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    const p = this.localPos(e);
    this.pointers.set(e.pointerId, p);
    e.preventDefault();

    if (this.pointers.size >= 2) {
      // Two-finger pinch-zoom around the centroid + two-finger pan.
      const it = this.pointers.values();
      const a = it.next().value as ActivePointer;
      const b = it.next().value as ActivePointer;
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;

      // Pan by centroid translation first (so a two-finger drag still pans).
      const panDx = cx - this.pinchCx;
      const panDy = cy - this.pinchCy;
      if (panDx !== 0 || panDy !== 0) {
        this.panX += panDx;
        this.panY += panDy;
      }
      // Then zoom around the (new) centroid by the distance ratio.
      const factor = dist / (this.pinchDist || dist);
      this.pinchCx = cx;
      this.pinchCy = cy;
      this.pinchDist = dist;
      if (factor !== 1 && Number.isFinite(factor) && factor > 0) {
        this.zoomAround(cx, cy, factor);
      } else {
        this.markDirty();
      }
      return;
    }

    // Single-pointer pan.
    const dx = p.x - this.lastX;
    const dy = p.y - this.lastY;
    this.lastX = p.x;
    this.lastY = p.y;
    if (dx !== 0 || dy !== 0) {
      this.panX += dx;
      this.panY += dy;
      this.markDirty();
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.delete(e.pointerId);
    try {
      this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* release is best-effort */
    }
    if (this.pointers.size === 1) {
      // Dropped from pinch back to single-pointer pan — reseed the anchor so the
      // remaining finger doesn't jump the view.
      const only = this.pointers.values().next().value as ActivePointer;
      this.lastX = only.x;
      this.lastY = only.y;
      this.canvas.classList.add("mopa-panning");
    } else if (this.pointers.size === 0) {
      this.canvas.classList.remove("mopa-panning");
    } else if (this.pointers.size >= 2) {
      this.refreshPinch();
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    this.zoomAround(e.clientX - rect.left, e.clientY - rect.top, this.wheelFactor(e));
  };

  private onDblClick = (e: MouseEvent): void => {
    e.preventDefault();
    this.fit();
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    const step = e.shiftKey ? KEY_PAN_STEP * 3 : KEY_PAN_STEP;
    switch (e.key) {
      case "r":
      case "R":
        this.rotateBy(e.shiftKey ? -15 : 15);
        break;
      case "0":
      case "Home":
        this.fit();
        break;
      case "1":
        this.resetZoom();
        break;
      case "+":
      case "=":
        this.zoomIn();
        break;
      case "-":
      case "_":
        this.zoomOut();
        break;
      case "ArrowLeft":
        this.panBy(step, 0);
        break;
      case "ArrowRight":
        this.panBy(-step, 0);
        break;
      case "ArrowUp":
        this.panBy(0, step);
        break;
      case "ArrowDown":
        this.panBy(0, -step);
        break;
      // Press-and-hold "show original" (compare affordance). Ignore autorepeat.
      case "o":
      case "O":
        if (!e.repeat) this.setShowOriginal(true);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.key === "o" || e.key === "O") {
      this.setShowOriginal(false);
    }
  };

  private onFocus = (): void => {
    this.host.classList.add("mopa-hint-visible");
  };

  private onBlur = (): void => {
    this.host.classList.remove("mopa-hint-visible");
    // Releasing focus cancels a held "show original".
    if (this.showOriginal) this.setShowOriginal(false);
  };

  // ------------------------------------------------- compare divider events ---

  private compareHandleLocalX(e: PointerEvent): number {
    const rect = this.host.getBoundingClientRect();
    const w = rect.width || 1;
    return clampNum((e.clientX - rect.left) / w, 0, 1);
  }

  private onCompareHandleDown = (e: PointerEvent): void => {
    if (!this.compareOn) return;
    this.compareDragging = true;
    this.compareHandlePointer = e.pointerId;
    try {
      this.compareHandle?.setPointerCapture(e.pointerId);
    } catch {
      /* capture best-effort */
    }
    e.preventDefault();
    e.stopPropagation();
  };

  private onCompareHandleMove = (e: PointerEvent): void => {
    if (!this.compareDragging || e.pointerId !== this.compareHandlePointer) return;
    this.compareFrac = this.compareHandleLocalX(e);
    this.updateCompareHandlePos();
    this.markDirty();
    e.preventDefault();
    e.stopPropagation();
  };

  private onCompareHandleUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.compareHandlePointer) return;
    this.compareDragging = false;
    this.compareHandlePointer = -1;
    try {
      this.compareHandle?.releasePointerCapture(e.pointerId);
    } catch {
      /* release best-effort */
    }
  };

  private onCompareHandleKey = (e: KeyboardEvent): void => {
    const stepFrac = e.shiftKey ? 0.1 : 0.02;
    let handled = true;
    switch (e.key) {
      case "ArrowLeft":
        this.compareFrac = clampNum(this.compareFrac - stepFrac, 0, 1);
        break;
      case "ArrowRight":
        this.compareFrac = clampNum(this.compareFrac + stepFrac, 0, 1);
        break;
      case "Home":
        this.compareFrac = 0;
        break;
      case "End":
        this.compareFrac = 1;
        break;
      default:
        handled = false;
    }
    if (handled) {
      this.updateCompareHandlePos();
      this.markDirty();
      e.preventDefault();
    }
  };

  private updateCompareHandlePos(): void {
    if (!this.compareHandle) return;
    const pct = clampNum(this.compareFrac, 0, 1) * 100;
    this.compareHandle.style.left = `${pct}%`;
    this.compareHandle.setAttribute("aria-valuenow", String(Math.round(pct)));
    this.compareHandle.setAttribute(
      "aria-valuetext",
      `${Math.round(pct)} percent processed`,
    );
  }
}
